import { Achievements, LevelCatalog, WyrmAlmanacRow, weakTo, wyrmAlmanac } from "@highmarch/app";
import { BalanceData, BossMechanic, DamageType, EnemyAffixDef, TowerArchetype, bossFirstClears, isAura, isBarracks, isHex } from "@highmarch/domain";
import { roleLine } from "../towerFacts";
import { enemyTraitLines } from "../enemyTraits";
import { RARES, isRareArchetype } from "../../rare/rareApi";

/**
 * The Almanac's content (FINISH_PLAN WP7, M20), DOM-free so it is tested without a page: every raider, boss, tower,
 * elite affix and Mythic power, with what the player has met or owns, their lifetime record (meta/stats.ts) and the
 * shared "weak to" advice (app counters.ts). Something not yet met keeps its name and details hidden until then.
 */

export interface AlmanacEnemy {
  readonly id: string;
  readonly name: string;
  readonly boss: boolean;
  /** Met: defeated at least once, or its first level is cleared. */
  readonly seen: boolean;
  readonly kills: number;
  /** The level it first comes at (a boss: the level it guards). */
  readonly firstLevel: number;
  readonly air: boolean;
  /** Armour as a whole percent (0 for none). */
  readonly armourPercent: number;
  /** Cells per second, one decimal. */
  readonly speed: string;
  readonly weak: { readonly towers: readonly TowerArchetype[]; readonly why: string };
  /** What its ability does (ui/enemyTraits.ts; the M21 enemies), empty for the others. */
  readonly traits: readonly string[];
}

export interface AlmanacTower {
  readonly id: string;
  readonly name: string;
  readonly owned: boolean;
  readonly unlockLevel: number;
  /** Its persistent level (the fortress's for the base). */
  readonly level: number;
  readonly kills: number;
  readonly damage: number;
  /** Mastery tier reached (index into the tiers; −1 for none) and the next one to reach. */
  readonly tier: number;
  /** The reached tier's name ("Silver"), "" for none. */
  readonly tierName: string;
  readonly next: { readonly name: string; readonly kills: number } | null;
  readonly damageType: string;
  readonly targets: string;
  /** Enemies whose "weak to" names this tower (best answers first). */
  readonly answers: readonly string[];
  /** What sets it apart, for the TOWERS_V2 towers (ui/towerFacts.ts roleLine); null for the others. */
  readonly role: string | null;
  /** Its two tier-IV branches (M21, docs/TOWERS_V2.md "Tier 4"): name, identity and both skills. */
  readonly branches: readonly AlmanacBranch[];
  /** The first level an Ascend card can come on (branches.json ascend.minLevel). */
  readonly ascendFrom: number;
}

export interface AlmanacBranch { readonly id: string; readonly name: string; readonly identity: string; readonly skills: readonly { readonly name: string; readonly description: string }[] }

export interface AlmanacAffix { readonly id: string; readonly name: string; readonly lines: readonly string[]; readonly kills: number; readonly seen: boolean; readonly firstLevel: number }
export interface AlmanacMythic { readonly id: string; readonly name: string; readonly towerId: string; readonly towerName: string; readonly description: string; readonly owned: boolean }

/** M36: a rare spawn's page entry — hidden until the player has seen it (a "Shiny hunter" collection of six). */
export interface AlmanacRare {
  readonly id: string;
  readonly name: string;
  readonly chapter: number;
  readonly seen: boolean;
  readonly kills: number;
  /** Its sparkle colour (0xRRGGBB), for the card's glow. */
  readonly spark: number;
}

export interface Almanac {
  /** M36: the six rares, chapter order. */
  readonly rares: readonly AlmanacRare[];
  /** M40: the Starfall Wyrm's row on the Rares page (app wyrmAlmanac), or null in a bundle without it. */
  readonly wyrm: WyrmAlmanacRow | null;
  readonly enemies: readonly AlmanacEnemy[];
  readonly bosses: readonly AlmanacEnemy[];
  readonly towers: readonly AlmanacTower[];
  readonly affixes: readonly AlmanacAffix[];
  readonly mythics: readonly AlmanacMythic[];
  readonly mastery: readonly { readonly id: string; readonly name: string; readonly kills: number }[];
}

/** Mastery trim class per tier index (achievements.json mastery: bronze, silver, gold): CSS only, the names are data. */
export const TRIMS = ["bronze", "silver", "gold"] as const;
export const trimOf = (tier: number): string => (tier >= 0 ? TRIMS[Math.min(tier, TRIMS.length - 1)] : "none");

const DAMAGE_TYPE: Readonly<Record<DamageType, string>> = { [DamageType.Physical]: "Physical", [DamageType.Magic]: "Magic", [DamageType.Burn]: "Burn" };
const PERCENT = 100;
/** A level past the campaign: "never met" for an affix no level uses. */
const NEVER = Number.MAX_SAFE_INTEGER;

/** What an elite affix does, in short lines from its numbers (enemies.json affixes). */
export function affixLines(B: BalanceData, a: EnemyAffixDef): string[] {
  const out: string[] = [];
  const less = (f: number) => Math.round((1 - f) * PERCENT);
  const pt = a.physicalTaken.toNumber(), mt = a.magicTaken.toNumber(), bt = a.burnTaken.toNumber(), sm = a.slowMult.toNumber(), hp = a.hpMult.toNumber();
  if (pt < 1) out.push(`Takes ${less(pt)}% less physical damage`);
  if (mt < 1) out.push(`Takes ${less(mt)}% less magic damage`);
  if (bt === 0) out.push("Burns do nothing"); else if (bt < 1) out.push(`Burns do ${less(bt)}% less`);
  if (sm < 1) out.push(sm === 0 ? "Can't be slowed" : `Slows work ${sm === 0.5 ? "half" : `${Math.round(sm * PERCENT)}%`} as well`);
  if (a.splitCount > 0 && a.splitInto >= 0) out.push(`Splits into ${a.splitCount} ${B.enemies[a.splitInto]?.name ?? "raiders"}${a.splitCount === 1 ? "" : "s"} when it falls`);
  if (hp > 1) out.push(`${Math.round((hp - 1) * PERCENT)}% more health`);
  if (a.pointsMult.toNumber() > 1) out.push("Worth more points");
  return out;
}

export function almanacModel(catalog: LevelCatalog, achievements: Achievements): Almanac {
  const B = catalog.balance, s = achievements.stats, cleared = catalog.highestLevelCleared;
  const bossLevel = new Map(bossFirstClears(B).map((b) => [b.bossId, b.level]));
  const enemy = (i: number): AlmanacEnemy => {
    const e = B.enemies[i], boss = e.bossMechanic !== BossMechanic.None;
    const firstLevel = boss ? bossLevel.get(e.id) ?? e.unlockLevel : e.unlockLevel;
    const kills = s.killsOf(e.id);
    return {
      id: e.id, name: e.name, boss, seen: kills > 0 || firstLevel <= cleared, kills, firstLevel, air: e.isAir,
      armourPercent: Math.round(e.armour.toNumber() * PERCENT), speed: e.speedCellsPerSecond.toDecimalString(1), weak: weakTo(B, e.id), traits: enemyTraitLines(B, e),
    };
  };
  const all = B.enemies.map((_, i) => enemy(i));
  const owned = catalog.ownedTowers(), tiers = B.achievements.mastery;
  const towers = B.towers.filter((t) => !t.isBase).map((t): AlmanacTower => {
    const kills = s.killsBy(t.id), tier = achievements.mastery(t.id);
    const next = tiers[tier + 1] ?? null;
    return {
      id: t.id, name: t.name, owned: owned[t.index], unlockLevel: t.unlockLevel, level: catalog.profile.towerLevels[t.index], kills, damage: s.damageBy(t.id),
      tier, tierName: tier >= 0 ? tiers[tier].name : "", next: next ? { name: next.name, kills: next.kills } : null,
      damageType: isAura(t) ? "Support" : isHex(t) ? "Curse" : DAMAGE_TYPE[t.damageType],
      targets: isHex(t) ? "Raiders in its aura" : isBarracks(t) ? "Blocks the road" : t.targetsAir && t.targetsGround ? "Ground & air" : t.targetsAir ? "Air only" : isAura(t) ? "Boosts towers in range" : "Ground only",
      answers: all.filter((e) => e.weak.towers.some((w) => w.id === t.id)).map((e) => e.id),
      role: roleLine(t),
      ascendFrom: B.branches.pacing.minLevel,
      branches: (B.branches.byTower[t.index] ?? []).map((i): AlmanacBranch => {
        const b = B.branches.branches[i];
        return { id: b.id, name: b.name, identity: b.identity, skills: b.skills.map((s) => ({ name: s.name, description: s.description })) };
      }),
    };
  });
  const firstElite = (affix: number): number => { const l = B.levels.find((lv) => lv.elites.some((r) => r.affix === affix)); return l ? l.id : NEVER; };
  const affixes = B.affixes.map((a): AlmanacAffix => {
    const kills = s.elites[a.id] ?? 0, firstLevel = firstElite(a.index);
    return { id: a.id, name: a.name, lines: affixLines(B, a), kills, seen: kills > 0 || firstLevel <= cleared, firstLevel };
  });
  const found = new Set(catalog.profile.gear.filter((g) => g.mythicEffect >= 0).map((g) => g.mythicEffect));
  const mythics = B.gear.mythicEffects.map((m): AlmanacMythic => ({
    id: m.id, name: m.name, towerId: m.towerId, towerName: m.towerId === "base" ? "Fortress" : B.towers.find((t) => t.id === m.towerId)?.name ?? m.towerId,
    description: m.description, owned: found.has(m.index),
  }));
  // M36: the rares are their own page (never a raider "met" just because its chapter is cleared)
  // M40: nor is the Starfall Wyrm (its own row: app wyrmAlmanac); Pets v2: nor the wild pup (a pet, not a raider)
  const plain = all.filter((e) => { const i = B.enemies.findIndex((x) => x.id === e.id); return !isRareArchetype(B, i) && !B.enemies[i].legend && B.enemies[i].wildPet === ""; });
  const rares = RARES.map((r): AlmanacRare => {
    const kills = B.enemies.some((e) => e.id === r.id) ? s.killsOf(r.id) : 0;
    return { id: r.id, name: r.name, chapter: r.chapter, seen: kills > 0 || s.hasSeenRare(r.id), kills, spark: r.sparkA };
  });
  const wyrm = wyrmAlmanac(B, s, (id) => catalog.ownsSkin(id));
  return { rares, wyrm, enemies: plain.filter((e) => !e.boss), bosses: plain.filter((e) => e.boss).sort((a, b) => a.firstLevel - b.firstLevel), towers, affixes, mythics, mastery: tiers };
}

/** The More row's note: raiders met and towers owned. */
export function almanacNote(a: Almanac): string {
  const met = [...a.enemies, ...a.bosses].filter((e) => e.seen).length, all = a.enemies.length + a.bosses.length;
  return `${met}/${all} foes · ${a.towers.filter((t) => t.owned).length}/${a.towers.length} towers`;
}
